// src/services/influencerSearchService.js
import { apiRequest } from "./apiService";

const DETAIL_FIRST_ATTEMPT_TIMEOUT_MS = 10_000;
const DETAIL_RETRY_TIMEOUT_MS = 18_000;
const DETAIL_RETRY_DELAY_MS = 300;
const SEARCH_TIMEOUT_MS = 15_000;

const sleepWithSignal = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error("Request was cancelled.");
      error.name = "AbortError";
      reject(error);
      return;
    }

    let timer = null;

    const onAbort = () => {
      if (timer !== null) window.clearTimeout(timer);
      signal?.removeEventListener?.("abort", onAbort);
      const error = new Error("Request was cancelled.");
      error.name = "AbortError";
      reject(error);
    };

    timer = window.setTimeout(() => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    }, ms);

    signal?.addEventListener?.("abort", onAbort, { once: true });
  });

const isTransientDetailFailure = (error) => {
  const status = Number(error?.status ?? error?.statusCode ?? 0);

  if ([408, 500, 502, 503, 504].includes(status)) return true;
  if (status === 0 && error?.name !== "AbortError") return true;

  return error instanceof TypeError;
};

const buildQueryString = (params) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  const query = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `?${query}`;
};

export const searchInfluencers = async ({
  platform,
  city,
  niche,
  tags,
  tagMode = "any",
  minFollowers,
  minMediaCount,
  minEngagementRate,
  onlyActive = true,
  minGeoConfidence,
  sort,
  seed,
  excludeSeen,
  limit = 50,
  lastKey,
  signal,
}) => {
  const qs = buildQueryString({
    platform,
    city,
    niche,
    tags,
    tag_mode: tagMode,
    min_followers: minFollowers,
    min_media_count: minMediaCount,
    min_engagement_rate: minEngagementRate,
    only_active: onlyActive != null ? String(Boolean(onlyActive)) : undefined,
    min_geo_confidence: minGeoConfidence || undefined,
    sort: sort || undefined,
    seed: seed || undefined,
    exclude_seen: excludeSeen != null ? String(Boolean(excludeSeen)) : undefined,
    limit,
    last_key: lastKey ? JSON.stringify(lastKey) : undefined,
  });

  return apiRequest(`/influencers/search${qs}`, "GET", null, {
    timeoutMs: SEARCH_TIMEOUT_MS,
    signal,
  });
};

export const getInfluencerDetails = async (influencerId, opts = {}) => {
  const endpoint = `/influencer/${encodeURIComponent(influencerId)}`;

  try {
    return await apiRequest(endpoint, "GET", null, {
      timeoutMs: DETAIL_FIRST_ATTEMPT_TIMEOUT_MS,
      signal: opts.signal,
    });
  } catch (error) {
    // A restored/idle browser tab can lose its first connection while the
    // secure cookie session is being revalidated. Retry one idempotent GET
    // only. Never retry authentication or CSRF failures.
    if (opts.signal?.aborted || !isTransientDetailFailure(error)) {
      throw error;
    }

    await sleepWithSignal(DETAIL_RETRY_DELAY_MS, opts.signal);

    return apiRequest(endpoint, "GET", null, {
      timeoutMs: DETAIL_RETRY_TIMEOUT_MS,
      signal: opts.signal,
    });
  }
};

export const enrichInstagram = async (payload) => apiRequest("/instagram/enrich", "POST", payload);
export const enrichYoutube = async (payload) => apiRequest("/youtube/enrich", "POST", payload);
export const importManualCandidates = async (payload) =>
  apiRequest("/admin/candidates/import", "POST", payload);


export const clearSeenInfluencers = async ({ platform, city, niche, sort, seed }) => {
  const qs = buildQueryString({ platform, city, niche, sort: sort || undefined, seed: seed || undefined });
  return apiRequest(`/influencers/seen${qs}`, "DELETE");
};
